import { Inject, Injectable, SimpleChanges } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AuthService } from '../../auth/auth/auth.service';
import { AppConfigService } from '../../shared/appconfig/appconfig.service';

import { Organization, DeletionSummary, DeletionResult } from '../../shared/models/core.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('BASE_URL') baseUrl: string
  ) {}

  private headers: HttpHeaders;

  public emptyOrg: Organization = {
    name: '',
    active: true,
    userActive: false,
    tier: '',
    phone: '',
    email: '',
    country: '',
    postalCode: '',
    crmId: '',
    billingId: '',
    supportId: '',
    id: '',
    readOnly: false,
    userId: '',
  };

  empty(): Organization {
    return this.emptyOrg;
  }

  getOrganizations() {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });

    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') + 'organization/',
      { headers: this.headers }
    );
  }

  getMyOrganization(): Observable<Organization> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });

    return this.http.get<Organization>(
      AppConfigService.settings.get('chatstyleapiServer') + 'organization/me',
      { headers: this.headers }
    );
  }

  updateOrganization(org: Organization) {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.post<string>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/' +
        org.id,
      org,
      { headers: this.headers }
    );
  }

  getUsers(): Observable<Organization[]> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });

    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/users',
      { headers: this.headers }
    );
  }

  getInvitations(): Observable<Organization[]> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });

    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations',
      { headers: this.headers }
    );
  }

  getMyInvitations(): Observable<Organization[]> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });

    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations/me',
      { headers: this.headers }
    );
  }

  setActive(id: string): Observable<any> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.post<string>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/' +
        id +
        '/active',
      {},
      { headers: this.headers }
    );
  }

  inviteUser(email: string): Observable<any> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.post<string>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invite/',
      { email: email },
      { headers: this.headers }
    );
  }

  updateUserActivation(org: Organization): Observable<Organization> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.post<Organization>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/users',
      org,
      { headers: this.headers }
    );
  }

  updateInvitationActivation(org: Organization): Observable<Organization> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.post<Organization>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations',
      org,
      { headers: this.headers }
    );
  }

  deleteUser(userId: string): Observable<any> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.delete(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/' +
        userId,
      { headers: this.headers }
    );
  }

  deleteInvitation(email: string): Observable<any> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.request('DELETE',
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invite',
      {
        headers: this.headers,
        body: { email }
      }
    );
  }

  getDeletionSummary(orgId: string): Observable<DeletionSummary> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.get<DeletionSummary>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'admin/organizations/' +
        orgId +
        '/deletion-summary',
      { headers: this.headers }
    );
  }

  deleteOrganization(orgId: string, confirmationName: string): Observable<DeletionResult> {
    this.headers = new HttpHeaders({
      Authorization: this.authService.getAuthorizationHeaderValue(),
    });
    return this.http.delete<DeletionResult>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'admin/organizations/' +
        orgId,
      {
        headers: this.headers,
        body: { confirmationName }
      }
    );
  }
}
